<template>
  <div class="app-versions-page">
    <div class="header">
      <h1>
        <i class="fas fa-mobile-alt"></i>
        Gerenciamento de Versões do App
      </h1>
      <p class="subtitle">Gerencie as versões do aplicativo móvel AB</p>
    </div>

    <!-- Seção de Upload e Registro de Nova Versão -->
    <div class="upload-section card">
      <h2>
        <i class="fas fa-cloud-upload-alt"></i>
        Publicar Nova Versão
      </h2>

      <div class="upload-steps">
        <!-- Passo 1: Upload do APK -->
        <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>Upload do APK</h3>
            <p>Selecione o arquivo APK para enviar</p>
            
            <div v-if="currentStep === 1" class="upload-area">
              <input 
                type="file" 
                ref="fileInput" 
                @change="handleFileSelect" 
                accept=".apk"
                style="display: none"
              />
              
              <div 
                v-if="!selectedFile"
                class="drop-zone"
                @click="$refs.fileInput.click()"
                @dragover.prevent
                @drop.prevent="handleFileDrop"
              >
                <i class="fas fa-cloud-upload-alt"></i>
                <p>Clique ou arraste o APK aqui</p>
                <span>Apenas arquivos .apk</span>
              </div>

              <div v-else class="file-selected">
                <i class="fas fa-file-archive"></i>
                <div class="file-info">
                  <strong>{{ selectedFile.name }}</strong>
                  <span>{{ formatFileSize(selectedFile.size) }}</span>
                </div>
                <button @click="clearFile" class="btn-remove">
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <button 
                v-if="selectedFile"
                @click="uploadApk" 
                class="btn btn-primary"
                :disabled="uploading"
              >
                <i class="fas fa-upload"></i>
                {{ uploading ? 'Enviando...' : 'Enviar APK' }}
              </button>

              <!-- Barra de progresso do upload -->
              <div v-if="uploading" class="upload-progress">
                <div class="progress-info">
                  <span class="progress-text">Enviando arquivo...</span>
                  <span class="progress-percentage">{{ uploadProgress }}%</span>
                </div>
                <div class="progress-bar">
                  <div class="progress-bar-fill" :style="{ width: uploadProgress + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Passo 2: Preencher Informações -->
        <div class="step" :class="{ active: currentStep === 2 }">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>Informações da Versão</h3>
            <p>Preencha os dados da nova versão</p>

            <form v-if="currentStep === 2" @submit.prevent="registerVersion" class="version-form">
              <div class="form-row">
                <div class="form-group">
                  <label>Nome da Versão *</label>
                  <input 
                    v-model="versionForm.versionName" 
                    type="text" 
                    placeholder="Ex: 1.0.60"
                    required
                  />
                </div>

                <div class="form-group">
                  <label>Código da Versão *</label>
                  <input 
                    v-model.number="versionForm.versionCode" 
                    type="number" 
                    placeholder="Ex: 486"
                    required
                  />
                  <small>Deve ser maior que a versão anterior</small>
                </div>
              </div>

              <div class="form-group">
                <label>Data de Lançamento *</label>
                <input 
                  v-model="versionForm.releaseDate" 
                  type="datetime-local" 
                  required
                />
              </div>

              <div class="form-group">
                <label>Notas de Lançamento</label>
                <textarea 
                  v-model="versionForm.releaseNotes" 
                  rows="6"
                  placeholder="Digite as novidades desta versão...&#10;&#10;Exemplo:&#10;🚀 Nova funcionalidade X&#10;🐛 Correção de bugs&#10;⚡ Melhorias de performance"
                ></textarea>
              </div>

              <div class="form-row">
                <div class="form-group checkbox-group">
                  <label>
                    <input type="checkbox" v-model="versionForm.isActive" />
                    <span>Versão Ativa</span>
                  </label>
                  <small>Se marcado, a versão ficará disponível para download</small>
                </div>

                <div class="form-group checkbox-group">
                  <label>
                    <input type="checkbox" v-model="versionForm.isRequired" />
                    <span>Atualização Obrigatória</span>
                  </label>
                  <small>Se marcado, os usuários serão forçados a atualizar</small>
                </div>
              </div>

              <div class="form-actions">
                <button type="button" @click="currentStep = 1" class="btn btn-secondary">
                  <i class="fas fa-arrow-left"></i>
                  Voltar
                </button>
                <button type="submit" class="btn btn-success" :disabled="registering">
                  <i class="fas fa-check"></i>
                  {{ registering ? 'Registrando...' : 'Publicar Versão' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de Versões Cadastradas -->
    <div class="versions-list card">
      <div class="card-header">
        <h2>
          <i class="fas fa-list"></i>
          Versões Cadastradas
        </h2>
        <button @click="loadVersions" class="btn btn-secondary btn-sm">
          <i class="fas fa-sync-alt"></i>
          Atualizar
        </button>
      </div>

      <div v-if="loadingVersions" class="loading">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando versões...</p>
      </div>

      <div v-else-if="versions.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>Nenhuma versão cadastrada ainda</p>
      </div>

      <div v-else class="versions-table">
        <table>
          <thead>
            <tr>
              <th>Versão</th>
              <th>Código</th>
              <th>Data</th>
              <th>Tamanho</th>
              <th>Status</th>
              <th>Obrigatória</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="version in paginatedVersions"
              :key="version.id"
              :class="{ latest: isLatestVersion(version) }"
            >
              <td>
                <strong>{{ version.versionName }}</strong>
                <span v-if="isLatestVersion(version)" class="badge badge-primary">Mais recente</span>
              </td>
              <td>{{ version.versionCode }}</td>
              <td>{{ formatDate(version.releaseDate) }}</td>
              <td>{{ formatFileSize(version.fileSize) }}</td>
              <td>
                <span class="badge" :class="version.isActive ? 'badge-success' : 'badge-secondary'">
                  {{ version.isActive ? 'Ativa' : 'Inativa' }}
                </span>
              </td>
              <td>
                <span v-if="version.isRequired" class="badge badge-warning">
                  <i class="fas fa-exclamation-triangle"></i>
                  Sim
                </span>
                <span v-else class="text-muted">Não</span>
              </td>
              <td>
                <div class="action-buttons">
                  <button 
                    @click="viewReleaseNotes(version)" 
                    class="btn-action btn-info"
                    title="Ver notas"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    @click="downloadVersion(version)" 
                    class="btn-action btn-primary"
                    title="Baixar APK"
                  >
                    <i class="fas fa-download"></i>
                  </button>
                  <button 
                    @click="toggleActive(version)" 
                    class="btn-action"
                    :class="version.isActive ? 'btn-warning' : 'btn-success'"
                    :title="version.isActive ? 'Desativar' : 'Ativar'"
                  >
                    <i class="fas" :class="version.isActive ? 'fa-pause' : 'fa-play'"></i>
                  </button>
                  <button 
                    @click="confirmDelete(version)" 
                    class="btn-action btn-danger"
                    title="Excluir"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} versões</span>
          </div>
          <div class="pagination-controls">
            <button
              class="btn-pagination"
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
              title="Primeira página"
            >
              <i class="fas fa-angles-left"></i>
            </button>
            <button
              class="btn-pagination"
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
              title="Página anterior"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button
              class="btn-pagination"
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
              title="Próxima página"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
            <button
              class="btn-pagination"
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
              title="Última página"
            >
              <i class="fas fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Notas de Lançamento -->
    <div v-if="showNotesModal" class="modal-overlay" @click="showNotesModal = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Notas de Lançamento - v{{ selectedVersion?.versionName }}</h3>
          <button @click="showNotesModal = false" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <pre class="release-notes-content">{{ selectedVersion?.releaseNotes || 'Sem notas de lançamento' }}</pre>
        </div>
      </div>
    </div>

    <!-- Link para página pública -->
    <div class="public-link card">
      <div class="public-link-content">
        <div class="public-link-header">
          <i class="fas fa-link"></i>
          <div class="public-link-text">
            <strong>Página Pública de Download</strong>
            <p>Compartilhe este link com os usuários para download manual</p>
          </div>
        </div>
        <a :href="publicDownloadUrl" target="_blank" class="download-link">
          <span class="url-text">{{ publicDownloadUrl }}</span>
          <i class="fas fa-external-link-alt"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { http } from '@/services/config';
import { useToast } from '@/composables/useToast';
import { useConfirm } from '@/composables/useConfirm';

export default {
  name: 'ViewAppVersions',
  setup() {
    const { success, error, warning, info } = useToast();
    const { confirm } = useConfirm();
    return { 
      toast: { success, error, warning, info },
      confirm 
    };
  },
  data() {
    return {
      currentStep: 1,
      selectedFile: null,
      uploadedFileName: '',
      uploading: false,
      uploadProgress: 0,
      registering: false,
      loadingVersions: true,
      versions: [],
      pageIndex: 1,
      pageSize: 10,
      showNotesModal: false,
      selectedVersion: null,
      versionForm: {
        versionName: '',
        versionCode: null,
        fileName: '',
        fileSize: 0,
        releaseNotes: '',
        releaseDate: new Date().toISOString().slice(0, 16),
        isActive: true,
        isRequired: false,
      },
    };
  },
  computed: {
    publicDownloadUrl() {
      return `${window.location.origin}/download`;
    },
    totalRows() {
      return this.versions.length;
    },
    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 0;
    },
    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
    },
    endItem() {
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },
    paginatedVersions() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.versions.slice(start, start + this.pageSize);
    },
    latestVersionId() {
      return this.versions[0]?.id ?? null;
    },
  },
  async mounted() {
    this.loadingVersions = true;
    await this.loadVersions();
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && file.name.endsWith('.apk')) {
        this.selectedFile = file;
      } else {
        this.toast.error('Por favor, selecione um arquivo APK válido');
      }
    },
    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file && file.name.endsWith('.apk')) {
        this.selectedFile = file;
      } else {
        this.toast.error('Por favor, selecione um arquivo APK válido');
      }
    },
    clearFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
    },
    async uploadApk() {
      if (!this.selectedFile) return;

      this.uploading = true;
      this.uploadProgress = 0;
      const formData = new FormData();
      formData.append('file', this.selectedFile);

      try {
        const response = await http.post('/api/v1/app/version/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutos (300.000ms) para upload de arquivos grandes
          onUploadProgress: (progressEvent) => {
            // Calcula a porcentagem do upload
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          },
        });

        this.uploadedFileName = response.data.fileName;
        this.versionForm.fileName = response.data.fileName;
        this.versionForm.fileSize = response.data.fileSize;

        // Extrair versão do nome do arquivo se possível (AB_v1.0.60.apk -> 1.0.60)
        const versionMatch = this.uploadedFileName.match(/v(\d+\.\d+\.\d+)/);
        if (versionMatch) {
          this.versionForm.versionName = versionMatch[1];
        }

        this.toast.success('APK enviado com sucesso!');
        this.currentStep = 2;
      } catch (error) {
        console.error('Erro ao fazer upload:', error);
        this.toast.error(
          error.response?.data?.message || 'Erro ao fazer upload do APK'
        );
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },
    async registerVersion() {
      this.registering = true;

      try {
        await http.post('/api/v1/app/version', this.versionForm);

        this.toast.success('Versão registrada com sucesso!');
        this.resetForm();
        this.pageIndex = 1;
        this.loadVersions();
      } catch (error) {
        console.error('Erro ao registrar versão:', error);
        this.toast.error(
          error.response?.data?.message || 'Erro ao registrar versão'
        );
      } finally {
        this.registering = false;
      }
    },
    async loadVersions() {
      this.loadingVersions = true;

      try {
        const response = await http.get('/api/v1/app/version/all');
        this.versions = response.data ?? [];
        if (this.pageIndex > this.totalPages && this.totalPages > 0) {
          this.pageIndex = this.totalPages;
        } else if (this.totalPages === 0) {
          this.pageIndex = 1;
        }
      } catch (error) {
        console.error('Erro ao carregar versões:', error);
        this.toast.error('Erro ao carregar versões');
      } finally {
        this.loadingVersions = false;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.pageIndex = page;
    },
    isLatestVersion(version) {
      return !!version?.id && version.id === this.latestVersionId;
    },
    async toggleActive(version) {
      try {
        await http.put('/api/v1/app/version', {
          id: version.id,
          isActive: !version.isActive,
        });

        version.isActive = !version.isActive;
        this.toast.success(
          `Versão ${version.isActive ? 'ativada' : 'desativada'} com sucesso`
        );
      } catch (error) {
        console.error('Erro ao alterar status:', error);
        this.toast.error('Erro ao alterar status da versão');
      }
    },
    async confirmDelete(version) {
      try {
        const confirmed = await this.confirm({
          title: 'Excluir Versão',
          message: `Tem certeza que deseja excluir a versão ${version.versionName}? Esta ação não pode ser desfeita.`
        });

        if (confirmed) {
          try {
            await http.delete(`/api/v1/app/version/${version.id}`);
            this.toast.success('Versão excluída com sucesso');
            this.loadVersions();
          } catch (error) {
            console.error('Erro ao excluir versão:', error);
            this.toast.error('Erro ao excluir versão');
          }
        }
      } catch {
        // Usuário cancelou
      }
    },
    viewReleaseNotes(version) {
      this.selectedVersion = version;
      this.showNotesModal = true;
    },
    downloadVersion(version) {
      window.open(version.downloadUrl, '_blank');
    },
    resetForm() {
      this.currentStep = 1;
      this.selectedFile = null;
      this.uploadedFileName = '';
      this.versionForm = {
        versionName: '',
        versionCode: null,
        fileName: '',
        fileSize: 0,
        releaseNotes: '',
        releaseDate: new Date().toISOString().slice(0, 16),
        isActive: true,
        isRequired: false,
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    formatFileSize(bytes) {
      const mb = bytes / (1024 * 1024);
      return `${mb.toFixed(2)} MB`;
    },
  },
};
</script>

<style scoped>
.app-versions-page {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  margin-bottom: 30px;
}

.header h1 {
  font-size: 2rem;
  color: var(--text-primary);
  margin: 0 0 10px 0;
  align-items: center;
  gap: 15px;
}

.header h1 i {
  color: var(--accent-color);
}

.subtitle {
  color: var(--text-secondary);
  margin: 0;
}

.card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 30px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--card-border);
  margin-bottom: 25px;
}

.card h2 {
  font-size: 1.4rem;
  color: var(--text-primary);
  margin: 0 0 25px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-steps {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.step {
  display: flex;
  gap: 20px;
  opacity: 0.5;
  pointer-events: none;
}

.step.active {
  opacity: 1;
  pointer-events: auto;
}

.step.completed {
  opacity: 0.8;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.step.active .step-number {
  background: var(--accent-color);
  color: white;
}

.step.completed .step-number {
  background: var(--success);
  color: white;
}

.step-content {
  flex: 1;
}

.step-content h3 {
  margin: 0 0 5px 0;
  color: var(--text-primary);
}

.step-content > p {
  margin: 0 0 15px 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.upload-area {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.drop-zone {
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.drop-zone:hover {
  border-color: var(--accent-color);
  background: var(--hover-color);
}

.drop-zone i {
  font-size: 3rem;
  color: var(--accent-color);
  margin-bottom: 15px;
}

.drop-zone p {
  margin: 0;
  font-weight: 600;
  color: var(--text-primary);
}

.drop-zone span {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.file-selected i {
  font-size: 2rem;
  color: white;
}

.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.file-info strong {
  color: var(--text-primary);
}

.file-info span {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.btn-remove {
  background: var(--danger-color);
  color: white;
  border: none;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-remove:hover {
  background: var(--danger-hover);
}

/* Barra de progresso do upload */
.upload-progress {
  margin-top: 15px;
  padding: 15px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-text {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9rem;
}

.progress-percentage {
  color: var(--accent-color);
  font-weight: 700;
  font-size: 1.1rem;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: var(--bg-tertiary);
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--accent-color), var(--primary-color));
  border-radius: 6px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-bar-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.version-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group input[type="datetime-local"],
.form-group textarea {
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s;
  background: var(--input-background);
  color: var(--text-primary);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--accent-color);
}

.form-group textarea {
  resize: vertical;
  font-family: inherit;
}

.form-group small {
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.checkbox-group input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 10px;
}

.btn {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
}

.btn-success {
  background: var(--success);
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #16a34a;
}

.btn-secondary {
  background: var(--secondary-btn-color);
  color: white;
}

.btn-secondary:hover {
  background: var(--secondary-btn-hover);
}

.btn-sm {
  padding: 8px 15px;
  font-size: 0.9rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.card-header h2 {
  margin: 0;
}

.loading, .empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-muted);
}

.loading i {
  font-size: 3rem;
  margin-bottom: 15px;
  color: var(--accent-color);
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 15px;
  color: var(--text-muted);
}

.versions-table {
  overflow-x: auto;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0 0;
  margin-top: 1rem;
  border-top: 1px solid var(--border-color);
  gap: 1rem;
  flex-wrap: wrap;
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-pagination {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary, var(--card-bg));
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-color);
  color: white;
  border-color: var(--accent-color);
}

.btn-pagination:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.page-info {
  font-weight: 600;
  color: var(--text-primary);
  min-width: 64px;
  text-align: center;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  text-align: left;
  padding: 12px;
  border-bottom: 2px solid var(--border-color);
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.9rem;
}

tbody tr {
  border-bottom: 1px solid var(--border-color);
  transition: background 0.2s;
}

tbody tr:hover {
  background: var(--hover-color);
}

tbody tr.latest {
  background: rgba(16, 185, 129, 0.1);
}

tbody td {
  padding: 15px 12px;
  color: var(--text-primary);
}

.badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
}

.badge-primary {
  background: var(--primary-color);
  color: white;
  margin-left: 8px;
}

.badge-success {
  background: var(--success);
  color: white;
}

.badge-secondary {
  background: var(--secondary-btn-color);
  color: white;
}

.badge-warning {
  background: var(--warning-color);
  color: white;
}

.text-muted {
  color: var(--text-muted);
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-action {
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  color: white;
}

.btn-action.btn-info {
  background: var(--info-color);
}

.btn-action.btn-info:hover {
  background: var(--info-hover);
}

.btn-action.btn-primary {
  background: var(--primary-color);
}

.btn-action.btn-primary:hover {
  background: var(--primary-hover);
}

.btn-action.btn-success {
  background: var(--success);
}

.btn-action.btn-success:hover {
  background: #16a34a;
}

.btn-action.btn-warning {
  background: var(--warning-color);
}

.btn-action.btn-warning:hover {
  background: var(--warning-hover);
}

.btn-action.btn-danger {
  background: var(--danger-color);
}

.btn-action.btn-danger:hover {
  background: var(--danger-hover);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--card-bg);
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--card-border);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.3s;
}

.btn-close:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
}

.release-notes-content {
  white-space: pre-wrap;
  font-family: inherit;
  margin: 0;
  color: var(--text-primary);
  line-height: 1.6;
}

.public-link {
  background: rgba(16, 185, 129, 0.1);
  border-left: 4px solid var(--success);
}

.public-link-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
}

.public-link-header {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.public-link-header i {
  font-size: 2.5rem;
  color: var(--success);
  flex-shrink: 0;
}

.public-link-text {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.public-link-text strong {
  color: var(--text-primary);
  font-size: 1.2rem;
  margin: 0;
}

.public-link-text p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.download-link {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--success);
  font-weight: 600;
  text-decoration: none;
  padding: 12px 20px;
  background: var(--card-bg);
  border: 2px solid var(--success);
  border-radius: 8px;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;
}

.download-link:hover {
  background: var(--success);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.download-link i {
  font-size: 1rem;
}

.url-text {
  max-width: 350px;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .app-versions-page {
    padding: 15px;
  }

  .card {
    padding: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .versions-table {
    font-size: 0.85rem;
  }

  .action-buttons {
    flex-wrap: wrap;
  }

  /* Responsividade da página pública */
  .public-link-content {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }

  .public-link-header {
    flex-direction: column;
    text-align: center;
  }

  .public-link-header i {
    font-size: 2rem;
  }

  .download-link {
    justify-content: center;
    width: 100%;
  }

  .url-text {
    max-width: 100%;
  }

  .pagination-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }
}
</style>

